import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChartLineUp, Newspaper, ArrowUpRight, ArrowDown, Minus, ArrowsClockwise, Bell, BellSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fetchNews, fetchRegionalNewsSummary } from "../lib/api";
import { relativeTime } from "../lib/time";
import SEO from "../components/SEO";

const ALERTS_KEY = "sentimeter_news_alerts_v1";
const loadAlerts = () => {
  try {
    const raw = localStorage.getItem(ALERTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};
const saveAlerts = (obj) => {
  try {
    localStorage.setItem(ALERTS_KEY, JSON.stringify(obj));
  } catch {
    // ignore quota errors
  }
};

const REGIONS = ["Global", "USA", "India", "Europe", "Asia"];

const tagFor = (s) => {
  if (s === "positive") return { c: "#34C759", icon: ArrowUpRight, label: "POS" };
  if (s === "negative") return { c: "#FF3B30", icon: ArrowDown, label: "NEG" };
  return { c: "#A1A1AA", icon: Minus, label: "NEU" };
};

function sentimentTilt(summary) {
  if (!summary) return { tilt: "neutral", pct: 0 };
  const p = summary.positive || 0;
  const n = summary.negative || 0;
  const total = p + n + (summary.neutral || 0);
  if (total === 0) return { tilt: "neutral", pct: 0 };
  const diff = p - n;
  const pct = Math.round((diff / total) * 100);
  if (pct > 8) return { tilt: "bullish", pct };
  if (pct < -8) return { tilt: "bearish", pct };
  return { tilt: "neutral", pct };
}

function RegionCard({ region, data, loading, onRefresh, alertOn, onToggleAlert, lastUpdated, nowTick }) {
  const summary = data?.news?.summary;
  const narrative = data?.summary?.narrative;
  const items = data?.news?.items || [];
  const tilt = sentimentTilt(summary);
  const tiltColor = tilt.tilt === "bullish" ? "#34C759" : tilt.tilt === "bearish" ? "#FF3B30" : "#A1A1AA";
  // nowTick is just a re-render trigger so "Xm ago" stays live; we don't read it.
  void nowTick;

  return (
    <article
      className="bg-surface border border-white/10 flex flex-col"
      data-testid={`region-card-${region}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: tiltColor }} />
          <h3 className="heading text-lg font-semibold tracking-tight">{region}</h3>
          {summary && (
            <span className="mono text-[10px] uppercase tracking-[0.14em] ml-1" style={{ color: tiltColor }}>
              {tilt.tilt} {tilt.pct >= 0 ? `+${tilt.pct}` : tilt.pct}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleAlert(region)}
            className={`transition-colors duration-150 ${
              alertOn ? "text-neutral_zone hover:text-yellow-300" : "text-white/40 hover:text-white"
            }`}
            data-testid={`alert-toggle-${region}`}
            data-alert-on={alertOn ? "true" : "false"}
            title={alertOn ? "alert on — notify on sentiment flip" : "alert off"}
          >
            {alertOn ? <Bell size={14} weight="fill" /> : <BellSlash size={14} weight="bold" />}
          </button>
          <button
            onClick={() => onRefresh(region)}
            disabled={loading}
            className="text-white/40 hover:text-white transition-colors duration-150 disabled:opacity-30"
            data-testid={`refresh-${region}`}
            title="refresh"
          >
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* AI narrative */}
      <div className="px-5 py-4 border-b border-white/10">
        <div className="overline mb-1.5">ai brief · claude sonnet 4.5</div>
        {loading && !narrative ? (
          <div className="space-y-2">
            <div className="h-3 bg-white/5 w-full" />
            <div className="h-3 bg-white/5 w-11/12" />
            <div className="h-3 bg-white/5 w-9/12" />
          </div>
        ) : (
          <p
            className="text-sm leading-relaxed text-white/85"
            data-testid={`narrative-${region}`}
          >
            {narrative || "—"}
          </p>
        )}
        {summary && (
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/5">
            <span className="mono text-xs text-bullish">{summary.positive || 0}↑</span>
            <span className="mono text-xs text-white/60">{summary.neutral || 0}=</span>
            <span className="mono text-xs text-bearish">{summary.negative || 0}↓</span>
            <span className="overline ml-auto" data-testid={`updated-${region}`}>
              {lastUpdated ? `updated ${relativeTime(lastUpdated)}` : `${items.length} headlines`}
            </span>
          </div>
        )}
      </div>

      {/* Headlines */}
      <div className="overflow-y-auto max-h-[360px]" data-testid={`headlines-${region}`}>
        {loading && items.length === 0 && (
          <div className="p-5 space-y-2">
            <div className="h-3 bg-white/5 w-full" />
            <div className="h-3 bg-white/5 w-10/12" />
            <div className="h-3 bg-white/5 w-9/12" />
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="overline p-6 text-center">no headlines</div>
        )}
        {items.slice(0, 8).map((h, i) => {
          const t = tagFor(h.sentiment);
          const Icon = t.icon;
          return (
            <a
              key={i}
              href={h.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-5 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors duration-150"
              data-testid={`headline-${region}-${i}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="flex flex-col items-center mt-0.5"
                  style={{ color: t.c, minWidth: 28 }}
                >
                  <Icon size={14} weight="bold" />
                  <span className="mono text-[9px] mt-0.5">{t.label}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm leading-snug">{h.title}</div>
                  <div className="mono text-[10px] text-white/40 mt-1 flex items-center gap-2">
                    <span className="truncate">{h.source || "news"}</span>
                    {h.published_iso && (
                      <>
                        <span className="text-white/20">·</span>
                        <span className="shrink-0 text-white/55" data-testid={`headline-time-${region}-${i}`}>
                          {relativeTime(h.published_iso)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </article>
  );
}

export default function NewsDashboard() {
  const navigate = useNavigate();
  const [regions, setRegions] = useState(() =>
    REGIONS.reduce((acc, r) => ({ ...acc, [r]: { loading: true, data: null } }), {})
  );
  const [alerts, setAlerts] = useState(() => loadAlerts());
  const [nowTick, setNowTick] = useState(0);
  const prevTilts = useRef({}); // region -> last seen tilt string

  // Re-render every 30s so "Xm ago" stays fresh without refetching data.
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const toggleAlert = useCallback((region) => {
    setAlerts((prev) => {
      const next = { ...prev, [region]: !prev[region] };
      saveAlerts(next);
      toast(next[region] ? `Alerts ON for ${region}` : `Alerts off for ${region}`, {
        description: next[region]
          ? "We'll ping you when this region flips sentiment."
          : undefined,
      });
      return next;
    });
  }, []);

  const loadRegion = useCallback(async (region) => {
    setRegions((prev) => ({ ...prev, [region]: { ...prev[region], loading: true } }));
    try {
      const [news, summary] = await Promise.all([
        fetchNews(region).catch(() => null),
        fetchRegionalNewsSummary(region).catch(() => null),
      ]);
      // Newest item's timestamp drives "updated Xm ago" badge.
      const newestIso =
        news?.items?.reduce((acc, it) => {
          const t = it?.published_iso ? new Date(it.published_iso).getTime() : 0;
          return t > acc ? t : acc;
        }, 0) || Date.now();
      setRegions((prev) => ({
        ...prev,
        [region]: { loading: false, data: { news, summary }, lastUpdated: newestIso },
      }));
    } catch (e) {
      setRegions((prev) => ({ ...prev, [region]: { loading: false, data: null } }));
    }
  }, []);

  useEffect(() => {
    // Stagger requests slightly to avoid hammering the backend / LLM concurrently
    REGIONS.forEach((r, i) => {
      setTimeout(() => loadRegion(r), i * 250);
    });
  }, [loadRegion]);

  // Detect sentiment-tilt flips per region; fire toast only if alert is ON for that region.
  useEffect(() => {
    REGIONS.forEach((r) => {
      const summary = regions[r]?.data?.news?.summary;
      if (!summary) return;
      const tilt = sentimentTilt(summary).tilt;
      const prev = prevTilts.current[r];
      if (prev && prev !== tilt && alerts[r]) {
        const icon =
          tilt === "bullish" ? "↑" : tilt === "bearish" ? "↓" : "=";
        toast(`${r}: ${prev.toUpperCase()} → ${tilt.toUpperCase()} ${icon}`, {
          description: "News sentiment flipped",
        });
      }
      prevTilts.current[r] = tilt;
    });
  }, [regions, alerts]);

  const refreshAll = () => {
    REGIONS.forEach((r, i) => setTimeout(() => loadRegion(r), i * 200));
  };

  // Cross-region summary tally
  const totals = REGIONS.reduce(
    (acc, r) => {
      const s = regions[r]?.data?.news?.summary;
      if (s) {
        acc.positive += s.positive || 0;
        acc.negative += s.negative || 0;
        acc.neutral += s.neutral || 0;
      }
      return acc;
    },
    { positive: 0, negative: 0, neutral: 0 }
  );
  const overallTilt = sentimentTilt(totals);
  const overallColor =
    overallTilt.tilt === "bullish" ? "#34C759" : overallTilt.tilt === "bearish" ? "#FF3B30" : "#A1A1AA";

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="news-page">
      <SEO
        title="Multi-Region Market News Dashboard"
        path="/news"
        description="AI-curated financial news with sentiment classification across Global, USA, India, Europe and Asia. Direct feeds from CNBC, Reuters, MarketWatch, Economic Times, Moneycontrol, BBC Business, SCMP and more — sorted newest first."
        keywords={["market news dashboard", "AI financial news", "Reuters sentiment", "CNBC sentiment", "Economic Times markets", "global market news", "India stock news", "Europe markets news"]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Multi-Region Market News",
          url: "https://sent-i-meter.com/news",
        }}
      />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10 px-4 md:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/70 hover:text-white"
            data-testid="news-back"
          >
            <ArrowLeft size={16} weight="bold" />
            <span className="overline">back</span>
          </button>
          <div className="w-px h-6 bg-white/10 mx-2" />
          <button
            onClick={() => navigate("/")}
            className="w-8 h-8 bg-white text-black flex items-center justify-center"
          >
            <ChartLineUp size={16} weight="bold" />
          </button>
          <div className="hidden sm:block">
            <div className="heading text-sm font-bold tracking-tight leading-none">
              NEWS DASHBOARD
            </div>
            <div className="overline mt-0.5 leading-none">multi-region market briefing</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshAll}
            className="flex items-center gap-1.5 px-2.5 py-1.5 mono text-[10px] uppercase tracking-[0.16em] border border-white/10 hover:border-white/40 text-white/70 hover:text-white transition-colors duration-150"
            data-testid="news-refresh-all"
          >
            <ArrowsClockwise size={12} weight="bold" />
            <span className="hidden md:inline">Refresh All</span>
          </button>
          <Newspaper size={18} weight="duotone" color="#FFCC00" />
        </div>
      </header>

      <main className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
        {/* Hero summary strip */}
        <section
          className="bg-surface border border-white/10 p-5 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-3"
          data-testid="news-hero"
        >
          <div>
            <div className="overline mb-1">cross-region pulse</div>
            <div className="flex items-baseline gap-3">
              <span
                className="heading text-3xl font-bold"
                style={{ color: overallColor }}
                data-testid="news-overall-tilt"
              >
                {overallTilt.tilt.toUpperCase()}
              </span>
              <span className="mono text-sm text-white/60">
                {overallTilt.pct >= 0 ? `+${overallTilt.pct}` : overallTilt.pct} net
              </span>
            </div>
            <div className="overline mt-1 text-white/50">
              aggregated from {REGIONS.length} regions
              {Object.values(alerts).filter(Boolean).length > 0 && (
                <span className="ml-3 inline-flex items-center gap-1 text-neutral_zone" data-testid="alerts-armed-count">
                  <Bell size={10} weight="fill" />
                  {Object.values(alerts).filter(Boolean).length} armed
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="mono text-2xl font-bold text-bullish" data-testid="news-total-pos">
                {totals.positive}
              </div>
              <div className="overline">positive</div>
            </div>
            <div className="text-center">
              <div className="mono text-2xl font-bold text-white/70" data-testid="news-total-neu">
                {totals.neutral}
              </div>
              <div className="overline">neutral</div>
            </div>
            <div className="text-center">
              <div className="mono text-2xl font-bold text-bearish" data-testid="news-total-neg">
                {totals.negative}
              </div>
              <div className="overline">negative</div>
            </div>
          </div>
        </section>

        {/* Grid of region cards */}
        <section
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6"
          data-testid="news-grid"
        >
          {REGIONS.map((r) => (
            <RegionCard
              key={r}
              region={r}
              data={regions[r]?.data}
              loading={regions[r]?.loading}
              lastUpdated={regions[r]?.lastUpdated}
              nowTick={nowTick}
              onRefresh={loadRegion}
              alertOn={!!alerts[r]}
              onToggleAlert={toggleAlert}
            />
          ))}
        </section>

        <footer className="border-t border-white/10 pt-4 mt-8 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="overline hover:text-white"
            data-testid="news-footer-back"
          >
            ← back to dashboard
          </button>
          <span className="overline">sources · google news rss · ai sentiment · claude sonnet 4.5</span>
        </footer>
      </main>
    </div>
  );
}
